import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useTheme, AccentColor } from '../../contexts/ThemeContext';
import { useIdentityReveal } from '../../contexts/IdentityRevealContext';
import { useUsage } from '../../hooks/useUsage';
import { useAuthFetch, QuotaExceededError } from '../../hooks/useAuthFetch';
import { SuggestionCarousel } from '../agent/SuggestionCarousel';
import { MCP_SAFE_AGENT_PROMPTS, TOPBAR_AGENT_PROMPT_ROTATION } from '../agent/mcpPromptSuggestions';
import './Layout.css';
import './LayoutV2.css';

/* ── SVG Icons (Lucide-style, 20×20) ── */
const IconLayout = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
);
const IconBriefcase = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
);
const IconUsers = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
);
const IconBot = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>
);
const IconSettings = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
);
const IconSun = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
);
const IconMoon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
);

const navItems = [
  { id: 'dashboard', label: 'Dashboard', path: '/', icon: IconLayout },
  { id: 'jobs', label: 'Jobs', path: '/jobs', icon: IconBriefcase },
  { id: 'talent', label: 'Talent Pool', path: '/talent', icon: IconUsers },
  { id: 'settings', label: 'Settings', path: '/settings', icon: IconSettings },
];

interface LayoutV2Props {
  children: React.ReactNode;
}

interface DrawerReportDownload {
  downloadPath: string;
  filename: string;
  expiresInSeconds?: number;
  sizeBytes?: number;
}

interface DrawerMessage {
  role: 'user' | 'assistant';
  content: string;
  reportDownload?: DrawerReportDownload;
}

const LayoutV2: React.FC<LayoutV2Props> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { org } = useTenant();
  const { mode, accent, setMode, setAccent } = useTheme();
  const { revealIdentity, remainingSeconds, canToggleReveal, toggleRevealIdentity } = useIdentityReveal();
  const { usage } = useUsage();
  const authFetch = useAuthFetch();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [agentInput, setAgentInput] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMinimized, setDrawerMinimized] = useState(false);
  const [drawerMessages, setDrawerMessages] = useState<DrawerMessage[]>([]);
  const [drawerSending, setDrawerSending] = useState(false);
  const [drawerSessionId, setDrawerSessionId] = useState<string | null>(null);
  const [drawerInput, setDrawerInput] = useState('');
  const [topbarPromptIndex, setTopbarPromptIndex] = useState(0);
  const drawerMessagesEndRef = useRef<HTMLDivElement>(null);
  const drawerInputRef = useRef<HTMLTextAreaElement>(null);
  const agentInputRef = useRef<HTMLInputElement>(null);
  const themePickerRef = useRef<HTMLDivElement>(null);

  const accentOptions: { value: AccentColor; label: string; color: string }[] = [
    { value: 'orange', label: 'Orange', color: '#fdba74' },
    { value: 'blue', label: 'Blue', color: '#93c5fd' },
    { value: 'green', label: 'Green', color: '#86efac' },
    { value: 'slate', label: 'Indigo', color: '#a5b4fc' },
  ];

  // Check admin status once on mount
  useEffect(() => {
    let cancelled = false;
    authFetch('/api/admin/check').then(res => {
      if (!cancelled && res.ok) return res.json();
    }).then(data => {
      if (!cancelled && data?.isAdmin) setIsAdmin(true);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [authFetch]);

  const handleNav = useCallback((path: string) => {
    // Direct hash change bypasses React Router's startTransition deferral
    // which can be starved by heavy renders (e.g. Whole Pipeline view).
    window.location.hash = '#' + path;
    setMobileOpen(false);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMobileOpen(false); setThemePickerOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Close theme picker on click outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (themePickerRef.current && !themePickerRef.current.contains(e.target as Node)) {
        setThemePickerOpen(false);
      }
    };
    if (themePickerOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [themePickerOpen]);

  // Auto-scroll drawer messages
  useEffect(() => {
    drawerMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [drawerMessages, drawerSending]);

  // Focus drawer input when opened
  useEffect(() => {
    if (drawerOpen && !drawerMinimized) {
      setTimeout(() => drawerInputRef.current?.focus(), 100);
    }
  }, [drawerOpen, drawerMinimized]);

  // Rotate topbar prompt suggestions for pre-click discovery.
  useEffect(() => {
    if (TOPBAR_AGENT_PROMPT_ROTATION.length <= 1) return;
    const intervalId = window.setInterval(() => {
      setTopbarPromptIndex((prev) => (prev + 1) % TOPBAR_AGENT_PROMPT_ROTATION.length);
    }, 3200);
    return () => window.clearInterval(intervalId);
  }, []);

  // Simple markdown renderer for drawer messages
  const renderMarkdown = (text: string) => {
    // Preserve line breaks for proper formatting
    let html = text
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="agent-code-block"><code>$2</code></pre>')
      .replace(/`([^`]+)`/g, '<code class="agent-inline-code">$1</code>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^### (.+)$/gm, '<h4 class="agent-h4">$1</h4>')
      .replace(/^## (.+)$/gm, '<h3 class="agent-h3">$1</h3>')
      .replace(/^# (.+)$/gm, '<h2 class="agent-h2">$1</h2>');

    // Convert numbered lists: "1. item" → <ol> blocks
    html = html.replace(/^(\d+)\. (.+)$/gm, '<oli>$2</oli>');
    html = html.replace(/((<oli>[\s\S]*?<\/oli>\n?)+)/g, (m) => `<ol class="agent-list">${m.replace(/oli>/g, 'li>')}</ol>`);

    // Convert bullet lists: "- item" → <ul> blocks
    html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
    html = html.replace(/((<li>[\s\S]*?<\/li>\n?)+)/g, (m) => `<ul class="agent-list">${m}</ul>`);

    // Convert remaining newlines to <br> for proper line spacing
    html = html.replace(/\n\n/g, '</p><p>');
    html = html.replace(/\n/g, '<br/>');
    html = `<p>${html}</p>`;
    html = html.replace(/<p>\s*<\/p>/g, '');
    return html;
  };

  const downloadAgentReport = useCallback(async (download: DrawerReportDownload) => {
    try {
      const res = await authFetch(download.downloadPath, {
        method: 'GET',
      });

      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(detail || `Download failed with status ${res.status}`);
      }

      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = download.filename || 'report.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setDrawerMessages(prev => [...prev, { role: 'assistant', content: `Report download failed. ${message}` }]);
    }
  }, [authFetch]);

  // Send message via SSE streaming to the drawer
  const sendDrawerMessage = useCallback(async (msg: string) => {
    if (!msg.trim() || drawerSending) return;
    const trimmed = msg.trim();
    setDrawerMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setDrawerSending(true);
    setDrawerInput('');

    const readErrorDetail = async (res: Response): Promise<string> => {
      try {
        const data = await res.json();
        const detail = data?.detail;
        if (typeof detail === 'string' && detail.trim()) return detail;
        if (detail?.message && typeof detail.message === 'string') return detail.message;
      } catch {
        // Ignore JSON parsing errors and fall back to status text.
      }
      return res.statusText || 'Request failed';
    };

    try {
      const res = await authFetch('/api/agent/chat/stream', {
        method: 'POST',
        body: JSON.stringify({ message: trimmed, session_id: drawerSessionId }),
      });

      if (!res.ok) {
        const streamError = await readErrorDetail(res);

        // Fallback to non-stream endpoint so chat remains usable even if SSE path fails.
        const fallbackRes = await authFetch('/api/agent/chat', {
          method: 'POST',
          body: JSON.stringify({ message: trimmed, session_id: drawerSessionId }),
        });

        if (fallbackRes.ok) {
          const data = await fallbackRes.json();
          if (data.sessionId) setDrawerSessionId(data.sessionId);
          setDrawerMessages(prev => {
            const updated: DrawerMessage[] = [
              ...prev,
              { role: 'assistant', content: data.response || 'No response generated.' },
            ];

            const downloads = Array.isArray(data.downloads) ? data.downloads : [];
            for (const item of downloads) {
              if (item?.downloadPath) {
                updated.push({
                  role: 'assistant',
                  content: 'Your report is ready to download.',
                  reportDownload: {
                    downloadPath: String(item.downloadPath),
                    filename: String(item.filename || 'match-report.pdf'),
                    expiresInSeconds: typeof item.expiresInSeconds === 'number' ? item.expiresInSeconds : undefined,
                    sizeBytes: typeof item.sizeBytes === 'number' ? item.sizeBytes : undefined,
                  },
                });
              }
            }

            return updated;
          });
          return;
        }

        const fallbackError = await readErrorDetail(fallbackRes);
        setDrawerMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: `Agent request failed (stream ${res.status}, fallback ${fallbackRes.status}). ${streamError}. ${fallbackError}`,
          },
        ]);
        return;
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder();
      let full = '';
      if (!reader) {
        setDrawerMessages(prev => [...prev, { role: 'assistant', content: 'Agent stream had no body. Please try again.' }]);
        return;
      }

      // Add placeholder assistant message
      setDrawerMessages(prev => [...prev, { role: 'assistant', content: '' }]);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const payload = line.slice(6);
            if (payload === '[DONE]') continue;
            try {
              const parsed = JSON.parse(payload);
              if (parsed.sessionId) {
                setDrawerSessionId(parsed.sessionId);
              } else if (parsed.toolCall) {
                // Agent is calling a tool — show thinking indicator
                setDrawerMessages(prev => {
                  const updated = [...prev];
                  if (updated[updated.length - 1]?.content === '' || updated[updated.length - 1]?.content === 'Thinking...') {
                    updated[updated.length - 1] = { role: 'assistant', content: 'Thinking...' };
                  }
                  return updated;
                });
              } else if (parsed.reportReady?.downloadPath) {
                const report = parsed.reportReady;
                setDrawerMessages(prev => {
                  const updated = [...prev];
                  const reportMessage: DrawerMessage = {
                    role: 'assistant',
                    content: 'Your report is ready to download.',
                    reportDownload: {
                      downloadPath: String(report.downloadPath),
                      filename: String(report.filename || 'match-report.pdf'),
                      expiresInSeconds: typeof report.expiresInSeconds === 'number' ? report.expiresInSeconds : undefined,
                      sizeBytes: typeof report.sizeBytes === 'number' ? report.sizeBytes : undefined,
                    },
                  };

                  if (updated.length > 0 && updated[updated.length - 1]?.role === 'assistant' && (updated[updated.length - 1]?.content === '' || updated[updated.length - 1]?.content === 'Thinking...')) {
                    updated[updated.length - 1] = reportMessage;
                  } else {
                    updated.push(reportMessage);
                  }
                  return updated;
                });
              } else if (parsed.token) {
                full += parsed.token;
                const captured = full;
                setDrawerMessages(prev => {
                  const updated = [...prev];
                  if (updated.length === 0) {
                    updated.push({ role: 'assistant', content: captured });
                    return updated;
                  }

                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, content: captured };
                  } else {
                    updated.push({ role: 'assistant', content: captured });
                  }
                  return updated;
                });
              } else if (parsed.text) {
                full = parsed.text;
                const captured = full;
                setDrawerMessages(prev => {
                  const updated = [...prev];
                  if (updated.length === 0) {
                    updated.push({ role: 'assistant', content: captured });
                    return updated;
                  }

                  const last = updated[updated.length - 1];
                  if (last?.role === 'assistant') {
                    updated[updated.length - 1] = { ...last, content: captured };
                  } else {
                    updated.push({ role: 'assistant', content: captured });
                  }
                  return updated;
                });
              }
            } catch {
              // Skip non-JSON stream chunks
            }
          }
        }
      }
    } catch (err) {
      if (err instanceof QuotaExceededError) {
        const quotaMessage = (err as Error).message || 'Usage limit reached.';
        setDrawerMessages(prev => [...prev, { role: 'assistant', content: quotaMessage }]);
      } else {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setDrawerMessages(prev => [...prev, { role: 'assistant', content: `Failed to reach agent. ${message}` }]);
      }
    } finally {
      setDrawerSending(false);
    }
  }, [authFetch, drawerSessionId, drawerSending]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const currentTopbarPrompt = TOPBAR_AGENT_PROMPT_ROTATION[topbarPromptIndex] || 'Ask anything...';

  const revealMinutes = Math.max(1, Math.ceil(remainingSeconds / 60));

  const renderNavItem = (item: { id: string; label: string; path: string; icon: React.FC }) => {
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
        onClick={() => handleNav(item.path)}
        title={collapsed ? item.label : undefined}
      >
        <span className="sidebar-item-icon"><Icon /></span>
        {!collapsed && <span className="sidebar-item-label">{item.label}</span>}
      </button>
    );
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar sidebar-v2 ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo + collapse toggle */}
        <div className="sidebar-logo">
          <button
            className="sidebar-toggle-btn"
            onClick={e => { e.stopPropagation(); setCollapsed(c => !c); }}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          {!collapsed && (
            <span className="sidebar-logo-name" onClick={() => handleNav('/')} style={{ cursor: 'pointer' }}>xRank</span>
          )}
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          <div className="sidebar-nav-group">
            {navItems.map(renderNavItem)}
            {isAdmin && renderNavItem({ id: 'admin', label: 'Admin', path: '/admin', icon: IconSettings })}
          </div>

          <div className="sidebar-nav-spacer" />
        </nav>

        {/* Usage (condensed) */}
        {usage && !collapsed && (
          <div className="sidebar-usage">
            {(() => {
              const fmt = (used: number, limit: number | undefined): string => {
                if (limit === undefined || limit === null) return `${used}`;
                if (limit === -1) return `${used}/∞`;
                return `${used}/${limit}`;
              };
              const ru = usage.usage?.resumeUploads;
              const aj = usage.usage?.activeJobs;
              const ag = usage.usage?.agentSessions;
              const QAS = 5;
              const rows: Array<{ key: string; label: string; value: string }> = [];
              rows.push({ key: 'uploads', label: 'Uploads', value: fmt(ru?.used ?? 0, ru?.monthly) });
              if (aj && aj.limit !== undefined) {
                rows.push({ key: 'jobs', label: 'Active jobs', value: fmt(aj.current, aj.limit) });
              }
              if (ag && ag.monthly !== 0) {
                rows.push({
                  key: 'agent',
                  label: 'Agent Q&As',
                  value: fmt((ag.used ?? 0) * QAS, ag.monthly === -1 ? -1 : (ag.monthly ?? 0) * QAS),
                });
              }
              return rows.map((row) => (
                <div key={row.key} className="sidebar-usage-row">
                  <span>{row.label}</span>
                  <span>{row.value}</span>
                </div>
              ));
            })()}
            <div className="sidebar-usage-plan">{usage.planDisplayName}</div>
          </div>
        )}


      </aside>

      {/* Mobile overlay */}
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className={`layout-main ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <header className="topbar">
          <button className="topbar-hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Toggle menu">
            <span className={`hamburger-line ${mobileOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${mobileOpen ? 'open' : ''}`} />
            <span className={`hamburger-line ${mobileOpen ? 'open' : ''}`} />
          </button>

          <div className="topbar-org" title={org?.name || ''}>{org ? org.name : ''}</div>

          {canToggleReveal && (
            <button
              className={`topbar-reveal-toggle ${revealIdentity ? 'active' : ''}`}
              onClick={() => void toggleRevealIdentity(!revealIdentity)}
              title={revealIdentity ? 'Turn off identity reveal' : 'Turn on identity reveal'}
            >
              {revealIdentity ? `Reveal: ON (${revealMinutes}m)` : 'Reveal: OFF'}
            </button>
          )}

          {/* Agent Prompt Box — triggers drawer (desktop) */}
          <div className="topbar-agent-wrapper">
            <div className="topbar-agent-input-row">
              <IconBot />
              <input
                ref={agentInputRef}
                className="topbar-agent-input"
                placeholder={currentTopbarPrompt}
                value={agentInput}
                onChange={e => setAgentInput(e.target.value)}
                onFocus={() => { if (!drawerOpen) { setDrawerOpen(true); setDrawerMinimized(false); } }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && agentInput.trim() && !drawerSending) {
                    e.preventDefault();
                    const msg = agentInput.trim();
                    setAgentInput('');
                    setDrawerOpen(true);
                    setDrawerMinimized(false);
                    sendDrawerMessage(msg);
                  }
                }}
              />
              {drawerSending && <span className="v2-spinner" style={{ width: 14, height: 14 }} />}
            </div>
          </div>

          {/* Agent icon button — mobile only */}
          <button
            className="topbar-agent-mobile-btn"
            onClick={() => { setDrawerOpen(true); setDrawerMinimized(false); }}
            aria-label="Open HR Agent"
            title="Ask HR Agent"
          >
            <IconBot />
          </button>

          <div className="theme-picker-wrapper" ref={themePickerRef}>
            <button
              className="topbar-theme-toggle"
              onClick={() => setThemePickerOpen(!themePickerOpen)}
              aria-label="Change theme"
              title="Theme settings"
            >
              <span className="theme-picker-swatch" style={{ background: accentOptions.find(a => a.value === accent)?.color }} />
              {mode === 'dark' ? <IconMoon /> : <IconSun />}
            </button>
            {themePickerOpen && (
              <div className="theme-picker-dropdown">
                <div className="theme-picker-section">
                  <span className="theme-picker-label">Accent</span>
                  <div className="theme-picker-colors">
                    {accentOptions.map(opt => (
                      <button
                        key={opt.value}
                        className={`theme-color-btn ${accent === opt.value ? 'active' : ''}`}
                        style={{ background: opt.color }}
                        onClick={() => setAccent(opt.value)}
                        title={opt.label}
                        aria-label={`${opt.label} accent color`}
                      />
                    ))}
                  </div>
                </div>
                <div className="theme-picker-section">
                  <span className="theme-picker-label">Mode</span>
                  <div className="theme-picker-modes">
                    <button
                      className={`theme-mode-btn ${mode === 'light' ? 'active' : ''}`}
                      onClick={() => setMode('light')}
                    >
                      Light
                    </button>
                    <button
                      className={`theme-mode-btn ${mode === 'dark' ? 'active' : ''}`}
                      onClick={() => setMode('dark')}
                    >
                      Dark
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {user && (
            <div className="topbar-user">
              {user.photoURL ? (
                <img src={user.photoURL} alt="" className="topbar-avatar" referrerPolicy="no-referrer" />
              ) : (
                <div className="topbar-avatar topbar-avatar-placeholder">
                  {(user.displayName || user.email || '?')[0].toUpperCase()}
                </div>
              )}
              <span className="topbar-username" title={user.displayName || user.email || 'User'}>{user.displayName || user.email?.split('@')[0] || 'User'}</span>
              <button className="topbar-signout" onClick={() => logout()}>Sign Out</button>
            </div>
          )}
        </header>

        <div className="layout-content">
          {canToggleReveal && revealIdentity && (
            <div className="identity-reveal-banner">
              Identity reveal is active for this session. Candidate names may be visible.
            </div>
          )}
          {children}
        </div>

        <footer className="app-global-footer">
          <span>Powered by Aram AI Labs</span>
        </footer>
      </div>

      {/* Agent Drawer — right-side sliding panel */}
      <div className={`agent-drawer ${drawerOpen && !drawerMinimized ? 'open' : ''}`}>
        <div className="agent-drawer-header">
          <div className="agent-drawer-title">
            <IconBot />
            <span>HR Assistant</span>
          </div>
          <div className="agent-drawer-actions">
            <button
              className="agent-drawer-btn"
              title="Open full chat"
              onClick={() => {
                navigate('/agent', {
                  state: drawerMessages.length >= 2
                    ? { query: drawerMessages[0]?.content, response: drawerMessages[1]?.content, sessionId: drawerSessionId }
                    : undefined,
                });
                setDrawerOpen(false);
                setDrawerMinimized(false);
              }}
            >
              ↗
            </button>
            <button
              className="agent-drawer-btn"
              title="New conversation"
              onClick={() => { setDrawerMessages([]); setDrawerSessionId(null); }}
            >
              +
            </button>
            <button
              className="agent-drawer-btn"
              title="Minimize"
              onClick={() => setDrawerMinimized(true)}
            >
              −
            </button>
            <button
              className="agent-drawer-btn close"
              title="Close"
              onClick={() => { setDrawerOpen(false); setDrawerMinimized(false); }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="agent-drawer-messages">
          {drawerMessages.length === 0 ? (
            <div className="agent-drawer-welcome">
              <div className="agent-drawer-welcome-icon">🤖</div>
              <h3>HR Assistant</h3>
              <p>Ask about your resumes, job descriptions, or find the best candidates.</p>
              <SuggestionCarousel
                prompts={MCP_SAFE_AGENT_PROMPTS}
                compact
                onSelect={(suggestion) => sendDrawerMessage(suggestion)}
              />
            </div>
          ) : (
            drawerMessages.map((msg, i) => (
              <div key={i} className={`agent-drawer-msg agent-drawer-msg-${msg.role}`}>
                <div className="agent-drawer-msg-avatar">{msg.role === 'user' ? '👤' : '🤖'}</div>
                <div className="agent-drawer-msg-body">
                  {msg.role === 'assistant' ? (
                    <>
                      <div className="agent-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                      {msg.reportDownload && (
                        <a
                          href={msg.reportDownload.downloadPath}
                          className="agent-report-download-link"
                          onClick={(event) => {
                            event.preventDefault();
                            const download = msg.reportDownload;
                            if (!download) return;
                            downloadAgentReport(download);
                          }}
                        >
                          Download PDF report
                        </a>
                      )}
                    </>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))
          )}
          {drawerSending && drawerMessages[drawerMessages.length - 1]?.role !== 'assistant' && (
            <div className="agent-drawer-msg agent-drawer-msg-assistant">
              <div className="agent-drawer-msg-avatar">🤖</div>
              <div className="agent-drawer-msg-body">
                <div className="agent-drawer-typing"><span /><span /><span /></div>
              </div>
            </div>
          )}
          <div ref={drawerMessagesEndRef} />
        </div>

        {/* Input */}
        <div className="agent-drawer-input-area">
          <div className="agent-drawer-input-row">
            <textarea
              ref={drawerInputRef}
              className="agent-drawer-textarea"
              placeholder="Ask anything..."
              value={drawerInput}
              onChange={e => setDrawerInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendDrawerMessage(drawerInput);
                }
              }}
              rows={1}
              disabled={drawerSending}
            />
            <button
              className="agent-drawer-send"
              onClick={() => sendDrawerMessage(drawerInput)}
              disabled={!drawerInput.trim() || drawerSending}
            >
              ➤
            </button>
          </div>
        </div>
      </div>

      {/* Minimized FAB — shows when drawer is minimized */}
      {drawerMinimized && (
        <button
          className="agent-fab"
          onClick={() => setDrawerMinimized(false)}
          title="Open HR Assistant"
        >
          🤖
          {drawerMessages.length > 0 && <span className="agent-fab-badge" />}
        </button>
      )}
    </div>
  );
};

export default LayoutV2;
